// Product page (sections/main-product.liquid): gallery, review carousel, variant picker, add to cart and sticky bar.
// Variants come from the product JSON in the section; the chosen variant goes into the form's hidden "id" input,
// the URL (?variant=) and a 'variant:change' event on document (detail: { variant }) for apps that listen.
(() => {
  const section = document.querySelector('[data-product-section]');
  if (!section) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const strings = window.MP?.strings || {};
  const product = JSON.parse(section.querySelector('[data-product-json]').textContent);
  const money = cents => {
    const format = window.MP?.moneyFormat || '{{amount_with_comma_separator}} €';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const grouped = sep => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: `${grouped(',')}.${decimals}`,
      amount_no_decimals: grouped(','),
      amount_with_comma_separator: `${grouped('.')},${decimals}`,
      amount_no_decimals_with_comma_separator: grouped('.')
    })[key] ?? value);
  };

  // Gallery: a scroll-snap track, so touch swipes work natively; thumbs and arrows follow it.
  const track = section.querySelector('.pdp-track');
  let goTo = () => {};
  if (track) {
    const slides = [...track.children];
    const thumbs = [...section.querySelectorAll('.pdp-thumbs button')];
    const counter = section.querySelector('.pdp-counter b');
    let current = 0;
    goTo = index => {
      const next = (index + slides.length) % slides.length;
      track.scrollTo({ left: next * track.clientWidth, behavior: reduce ? 'instant' : 'smooth' });
    };
    const sync = () => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      if (index === current) return;
      current = index;
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      const strip = thumbs[index]?.parentElement;
      const thumb = thumbs[index];
      if (strip && thumb) {
        const vertical = strip.scrollHeight > strip.clientHeight;
        const start = vertical ? thumb.offsetTop - strip.offsetTop : thumb.offsetLeft - strip.offsetLeft;
        const size = vertical ? thumb.offsetHeight : thumb.offsetWidth;
        const view = vertical ? strip.clientHeight : strip.clientWidth;
        const scroll = vertical ? strip.scrollTop : strip.scrollLeft;
        const target = start < scroll ? start : start + size > scroll + view ? start + size - view : scroll;
        strip.scrollTo({ [vertical ? 'top' : 'left']: target, behavior: reduce ? 'instant' : 'smooth' });
      }
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
      // Videos stop when their slide leaves the stage.
      slides.forEach((slide, i) => { if (i !== index) slide.querySelector('video')?.pause(); });
    };
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
    section.querySelector('.pdp-prev')?.addEventListener('click', () => goTo(current - 1));
    section.querySelector('.pdp-next')?.addEventListener('click', () => goTo(current + 1));
    track.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(current + (event.key === 'ArrowRight' ? 1 : -1));
    });
  }

  // Review card carousel inside the buy box.
  // Slides are read on every step: reviews-live.js swaps in this pack's REVIEWS.io reviews ('reviews:loaded').
  const box = section.querySelector('.pdp-quotes');
  if (box) {
    const quotes = () => [...box.querySelectorAll('.pdp-quote')];
    let active = 0;
    let timer;
    const show = index => {
      const list = quotes();
      if (!list.length) return;
      list.forEach(quote => { quote.classList.remove('is-active'); quote.hidden = true; });
      active = (index + list.length) % list.length;
      list[active].hidden = false;
      list[active].classList.add('is-active');
    };
    const restart = () => { clearInterval(timer); if (!reduce) timer = setInterval(() => show(active + 1), 6000); };
    box.querySelector('.pdp-quotes-prev').addEventListener('click', () => { show(active - 1); restart(); });
    box.querySelector('.pdp-quotes-next').addEventListener('click', () => { show(active + 1); restart(); });
    box.addEventListener('pointerenter', () => clearInterval(timer));
    box.addEventListener('pointerleave', restart);
    box.addEventListener('focusin', () => clearInterval(timer));
    box.addEventListener('reviews:loaded', () => { show(0); restart(); });
    restart();
  }

  // Variant picker.
  const buy = section.querySelector('.pdp-buy');
  const form = document.getElementById(buy.dataset.formId);
  if (!form) return;
  const idInput = form.querySelector('[data-variant-id]');
  const addButton = form.querySelector('.pdp-add');
  const addLabel = addButton.querySelector('.pdp-add-label');
  const fieldsets = [...buy.querySelectorAll('.pdp-sizes[data-option-index]')];
  const error = buy.querySelector('.pdp-size-error');
  const sticky = document.querySelector(`.pdp-sticky[data-form-id="${buy.dataset.formId}"]`);
  const stickySelect = sticky?.querySelector('.pdp-sticky-select');
  const radios = index => [...fieldsets[index].querySelectorAll('input[type=radio]')];
  const chosen = () => fieldsets.map((_, i) => radios(i).find(input => input.checked)?.value ?? null);
  const complete = () => chosen().every(value => value !== null);
  const findVariant = values => product.variants.find(variant => variant.options.every((option, i) => option === values[i]));

  const paintAvailability = () => {
    const values = chosen();
    fieldsets.forEach((fieldset, i) => radios(i).forEach(input => {
      // A value counts as available if some available variant has it together with the other current choices.
      const ok = product.variants.some(variant => variant.available && variant.options[i] === input.value
        && variant.options.every((option, j) => j === i || values[j] === null || values[j] === option));
      input.nextElementSibling.classList.toggle('is-gone', !ok);
    }));
  };
  const update = () => {
    paintAvailability();
    const values = chosen();
    if (stickySelect && fieldsets.length === 1) stickySelect.value = values[0] || '';
    // Products without option pickers (one variant, e.g. SPECIAL PACKs): the server already rendered the state.
    if (!fieldsets.length) return;
    if (!complete()) return;
    fieldsets.forEach(fieldset => fieldset.classList.remove('is-error'));
    if (error) error.hidden = true;
    const variant = findVariant(values);
    if (!variant) {
      addButton.disabled = true;
      addLabel.textContent = strings.soldOut || addLabel.textContent;
      return;
    }
    idInput.value = variant.id;
    addButton.disabled = !variant.available;
    addLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
    // Sold-out size: offer the restock notifier right under the buttons, with this size.
    const sizeRestock = section.querySelector('[data-size-restock]');
    if (sizeRestock) {
      sizeRestock.hidden = variant.available;
      sizeRestock.dataset.size = values[values.length - 1] || '';
    }
    const price = section.querySelector('[data-price]');
    if (price) {
      price.querySelector('.pdp-price-now').textContent = money(variant.price);
      const was = price.querySelector('.pdp-price-was');
      was.hidden = !(variant.compare_at_price > variant.price);
      if (!was.hidden) was.textContent = money(variant.compare_at_price);
    }
    sticky?.querySelector('.pdp-sticky-price')?.replaceChildren(money(variant.price));
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(null, '', url);
    if (variant.featured_media && track) {
      const slide = [...track.children].findIndex(el => el.dataset.mediaId === String(variant.featured_media.id));
      if (slide > -1) goTo(slide);
    }
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, product } }));
  };
  buy.addEventListener('change', event => { if (event.target.matches('input[type=radio]')) update(); });
  stickySelect?.addEventListener('change', () => {
    const radio = radios(0).find(input => input.value === stickySelect.value);
    if (radio) { radio.checked = true; update(); }
  });
  update();

  const requireChoice = () => {
    if (complete()) return true;
    const missing = fieldsets.find((_, i) => chosen()[i] === null);
    missing.classList.remove('is-error');
    void missing.offsetWidth; // restart the shake on repeated clicks
    missing.classList.add('is-error');
    if (error) error.hidden = false;
    missing.scrollIntoView({ block: 'center', behavior: reduce ? 'instant' : 'smooth' });
    missing.querySelector('input').focus({ preventScroll: true });
    return false;
  };
  // Express checkout buttons read the form's variant id; without a chosen size they must not start.
  form.querySelector('.shopify-payment-button')?.addEventListener('click', event => {
    if (requireChoice()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  let resetTimer;
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (!requireChoice()) return;
    if (window.mpTeamPicker && !window.mpTeamPicker.validate(buy)) return;
    const variant = product.variants.find(entry => String(entry.id) === idInput.value);
    const excluded = [...document.querySelectorAll(`input[form="${form.id}"][name^="properties["]`)].map(input => input.value.trim()).filter(Boolean);
    const meta = [variant && !variant.title.startsWith('Default') ? variant.title : '', excluded.length ? `ohne ${excluded.join(', ')}` : ''].filter(Boolean).join(' · ');
    const stickyAdd = sticky?.classList.contains('is-visible') ? sticky.querySelector('.pdp-sticky-add') : null;
    addButton.disabled = true;
    window.mpCart.addWithFeedback({
      payload: new FormData(form),
      button: stickyAdd || addButton,
      name: product.title,
      meta,
      image: variant?.featured_image?.src || product.featured_image
    }).then(() => {
      addButton.classList.add('is-added');
      addLabel.textContent = `✓ ${strings.added || ''}`.trim();
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => { addButton.classList.remove('is-added'); addLabel.textContent = strings.addToCart; }, 2200);
    }).catch(() => {}).finally(() => { addButton.disabled = false; });
  });

  // Size chart dialog (snippets/size-chart.liquid).
  const chart = document.querySelector('.size-chart');
  document.addEventListener('click', event => {
    if (event.target.closest('[data-size-chart]')) chart?.showModal();
    else if (chart && (event.target === chart || event.target.closest('.size-chart-close'))) chart.close();
  });

  // Sticky bar: appears once the main button has scrolled out above the viewport, steps aside at the footer.
  if (sticky && 'IntersectionObserver' in window) {
    let past = false;
    let atFooter = false;
    const set = () => {
      const show = past && !atFooter;
      sticky.classList.toggle('is-visible', show);
      sticky.toggleAttribute('inert', !show);
      sticky.setAttribute('aria-hidden', String(!show));
    };
    // Desktop: once the main button has scrolled out above. Phones: as soon as the page is scrolled a little and
    // the main button is not on screen (also before it is reached), so buying is always one tap away.
    const phone = matchMedia('(max-width: 700px)');
    let offscreen = false;
    const decide = () => { past = phone.matches ? offscreen && scrollY > 260 : past; set(); };
    new IntersectionObserver(([entry]) => {
      offscreen = !entry.isIntersecting;
      if (!phone.matches) past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      decide();
    }).observe(addButton);
    addEventListener('scroll', () => { if (phone.matches) decide(); }, { passive: true });
    const footer = document.querySelector('.section-footer, footer');
    if (footer) new IntersectionObserver(([entry]) => { atFooter = entry.isIntersecting; set(); }).observe(footer);
    sticky.querySelector('.pdp-sticky-add').addEventListener('click', () => form.requestSubmit());
  }

  // Jersey wall rows (product-wall section) loop seamlessly: each track holds its tiles twice.
  if (!reduce) {
    const echo = (row, tile) => {
      const copy = tile.cloneNode(true);
      copy.removeAttribute('data-shopify-editor-block');
      copy.setAttribute('aria-hidden', 'true');
      (copy.tagName === 'IMG' ? [copy] : [...copy.querySelectorAll('img')]).forEach(img => { img.alt = ''; img.loading = 'eager'; });
      row.append(copy);
    };
    document.querySelectorAll('.pdp-wall-track').forEach(row => {
      const base = [...row.children];
      if (!base.length) return;
      const set = base.reduce((sum, tile) => sum + tile.getBoundingClientRect().width + 16, 0) || 1;
      for (let width = set; width < innerWidth + set; width += set) base.forEach(tile => echo(row, tile));
      [...row.children].forEach(tile => echo(row, tile));
    });
  }

  // "Noch ein PACK?": Shopify product recommendations, rendered by the section itself.
  document.querySelectorAll('[data-recommendations-url]').forEach(holder => {
    fetch(holder.dataset.recommendationsUrl).then(r => r.text()).then(html => {
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations-url]');
      if (next && next.querySelector('.shop-card')) holder.replaceWith(next);
    }).catch(() => {});
  });
})();
